import { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, X } from "lucide-react";
import { track } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGERENCIAS = ["¿Qué dron me conviene?", "Servicios con drones", "Soporte técnico", "Curso virtual de Academy"];

export default function ChatIA() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "¡Hola! Soy CERTERO AI. Pregúnteme por nuestros drones, servicios, soporte técnico o el curso de Academy." }]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const sidRef = useRef(null);
  const finRef = useRef(null);

  if (!sidRef.current) {
    sidRef.current = localStorage.getItem("certero_chat_sid") || crypto.randomUUID();
    localStorage.setItem("certero_chat_sid", sidRef.current);
  }

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs, open]);

  const enviar = async (texto) => {
    const mensaje = (texto ?? input).trim();
    if (!mensaje || thinking) return;
    setInput("");
    setThinking(true);
    track("chat_ia_mensaje", {});
    setMsgs((m) => [...m, { role: "user", text: mensaje }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sidRef.current, mensaje }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const bloques = buf.split("\n\n");
        buf = bloques.pop();
        for (const b of bloques) {
          if (!b.startsWith("data: ")) continue;
          const ev = JSON.parse(b.slice(6));
          if (ev.type === "delta") {
            setMsgs((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + ev.texto };
              return c;
            });
          } else if (ev.type === "error") {
            setMsgs((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", text: ev.texto };
              return c;
            });
          }
        }
      }
    } catch {
      setMsgs((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: "Hubo un problema de conexión. Escríbenos por WhatsApp al +591 75371335." };
        return c;
      });
    } finally {
      setThinking(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-ia-btn"
          onClick={() => { setOpen(true); track("chat_ia_abrir", {}); }}
          className="fixed bottom-5 left-5 z-[70] flex h-14 items-center gap-2 rounded-full border border-certero/50 bg-abyss/90 px-5 font-display text-xs font-bold uppercase tracking-[0.15em] text-certero shadow-[0_8px_30px_rgba(59,170,52,0.25)] backdrop-blur-md transition-transform duration-300 hover:scale-105 active:scale-95"
          aria-label="Abrir asistente CERTERO AI"
        >
          <Sparkles className="h-4 w-4" /> CERTERO AI
        </button>
      )}

      {open && (
        <div
          data-testid="chat-ia-panel"
          className="fixed bottom-5 left-5 z-[70] flex h-[70vh] max-h-[600px] w-[92vw] max-w-sm flex-col border border-hud bg-surface shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-hud bg-abyss/80 px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-certero/50 bg-certero/10">
                <Bot className="h-4 w-4 text-certero" />
              </span>
              <div>
                <p className="font-display text-sm font-bold uppercase tracking-wide text-mist">CERTERO AI</p>
                <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-certero">
                  <span className="h-1.5 w-1.5 rounded-full bg-certero motion-safe:animate-pulse" /> En línea
                </p>
              </div>
            </div>
            <button
              data-testid="chat-ia-close"
              onClick={() => setOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-hud text-mist/70 transition-colors duration-300 hover:border-certero hover:text-certero"
              aria-label="Cerrar asistente"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-ia-messages">
            {msgs.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-ia-msg-${m.role}`}
                className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-certero/15 border border-certero/40 text-mist"
                    : "border border-hud bg-abyss text-mist/85"
                }`}
              >
                {m.text || (thinking && i === msgs.length - 1 ? "..." : "")}
              </div>
            ))}
            <div ref={finRef} />
          </div>

          <div className="border-t border-hud p-3">
            <div className="mb-2 flex gap-2 overflow-x-auto pb-1">
              {SUGERENCIAS.map((s) => (
                <button
                  key={s}
                  data-testid="chat-ia-sugerencia"
                  onClick={() => enviar(s)}
                  disabled={thinking}
                  className="shrink-0 border border-hud px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-mist/70 transition-colors duration-300 hover:border-certero hover:text-certero disabled:opacity-40"
                >
                  {s}
                </button>
              ))}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); enviar(); }}
              className="flex items-center gap-2"
            >
              <input
                data-testid="chat-ia-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu pregunta..."
                maxLength={1000}
                className="h-11 flex-1 border border-hud bg-abyss px-4 text-sm text-mist placeholder:text-smoke/60 outline-none transition-colors duration-300 focus:border-certero"
              />
              <button
                type="submit"
                data-testid="chat-ia-send"
                disabled={thinking || !input.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-certero text-ink transition-[background-color,opacity] duration-300 hover:bg-[#4bc443] disabled:opacity-40"
                aria-label="Enviar mensaje"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
